import React, { useCallback, useLayoutEffect, useState } from 'react';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import { AppState } from 'reducers/reducers';
import { selectedSpotInfo } from 'selectors';
import { CarTrackingStatus } from 'types/CarTracking';
import usePaginatedFiltersWithSearch from 'hooks/usePaginatedFiltersWithSearch';
import { isValidDateForCarTrackingTable, scrollMainToTop } from 'utils/helpers';
import { RentalType } from 'utils/constants';
import Toggle from 'components/AGForm/Inputs/Toggle/Toggle';
import InfoTooltip from 'components/Dashboard/SpotCard/InfoTooltip/InfoTooltip';
import DashboardContent from 'components/Dashboard/DashboardContent/DashboardContent';
import DriverLog from './Components/DriverLog/DriverLog';
import TopSection from './Components/TopSection/TopSection';
import StatsSection from './Components/StatsSection/StatsSection';
import { VisibilityPageSection } from 'types';
import 'mapbox-gl/dist/mapbox-gl.css';
import { getVisibilityPageSections } from './utils';
import styles from './VisibilityContainer.module.css';

type VisibilityFilters = {
    date: string;
    status: CarTrackingStatus;
    rentalType: RentalType;
};

type UseVisibilityPageCommonOptions = {
    spotUuid?: string;
    push: (...args: any[]) => any;
};

type VisibilityLayoutProps = {
    isSuperuser: boolean;
    isDemo: boolean;
    superuserView: boolean;
    onToggleDemo: () => void;
    onToggleSuperuserView: () => void;
    children: React.ReactNode;
};

export function useVisibilityPageCommon({ spotUuid, push }: UseVisibilityPageCommonOptions) {
    const [superuserView, setSuperUserView] = useState(false);
    const [isDemo, setDemoMode] = useState(false);
    const { filters, urlUpdater } = usePaginatedFiltersWithSearch<VisibilityFilters>({
        filterValidators: {
            date(current) {
                return current && isValidDateForCarTrackingTable(current) ? current : new Date().toLocaleDateString();
            },
            status(current) {
                return current && Object.values(CarTrackingStatus).includes(current as CarTrackingStatus)
                    ? current
                    : '';
            },
            rentalType(current) {
                if (!current) return '';
                const rentalTypes = current.split(',').map((rt) => {
                    if (Object.values(RentalType).includes(rt as RentalType)) return rt;
                    return null;
                });
                return rentalTypes.filter((rt) => rt !== null).join(',');
            },
        },
        push,
    });

    useLayoutEffect(() => {
        scrollMainToTop();
    }, [spotUuid]);

    return {
        superuserView,
        setSuperUserView,
        isDemo,
        setDemoMode,
        filters,
        urlUpdater,
    };
}

function VisibilityLayout({
    isSuperuser,
    isDemo,
    superuserView,
    onToggleDemo,
    onToggleSuperuserView,
    children,
}: VisibilityLayoutProps): JSX.Element {
    return (
        <DashboardContent
            title="Visibility"
            actionItem={
                isSuperuser ? (
                    <div className={styles.superuserToggle}>
                        <div>
                            <Toggle label="Demo" checked={isDemo} onChange={onToggleDemo} />
                        </div>
                        <div>
                            <Toggle label="Extended View" checked={superuserView} onChange={onToggleSuperuserView} />
                            <InfoTooltip text="Enable extended Superuser view for more information" />
                        </div>
                    </div>
                ) : undefined
            }
        >
            <div className={styles.visibilityPage}>{children}</div>
        </DashboardContent>
    );
}

type VisibilityContainerOwnProps = {
    useVisibilityPageCommonHook?: typeof useVisibilityPageCommon;
};

type VisibilityContainerProps = VisibilityContainerOwnProps &
    ReturnType<typeof mapStateToProps> &
    typeof mapDispatchToProps;

export function VisibilityContainerComponent({
    spot,
    profile,
    push,
    useVisibilityPageCommonHook,
}: VisibilityContainerProps): JSX.Element {
    const [isPropertyViewVisible, setIsPropertyViewVisible] = useState(true);
    const resolveVisibilityPageCommon = useVisibilityPageCommonHook ?? useVisibilityPageCommon;
    const { superuserView, setSuperUserView, isDemo, setDemoMode, filters, urlUpdater } = resolveVisibilityPageCommon({
        spotUuid: spot?.uuid,
        push,
    });

    const isSpotLoading = !spot;
    const visibilityPageSections = spot ? getVisibilityPageSections(spot) : [];
    const areCamerasEnabled = visibilityPageSections.includes(VisibilityPageSection.CAMERAS);
    const isStatsEnabled = visibilityPageSections.includes(VisibilityPageSection.STATS);
    const isDriverLogEnabled = visibilityPageSections.includes(VisibilityPageSection.DRIVER_LOGS);
    const shouldRenderTopSection = areCamerasEnabled || isDemo;
    const shouldRenderStatsSection = isStatsEnabled || isDemo;
    const shouldRenderDriverLog = isDriverLogEnabled || isDemo;
    const shouldRenderVisibilityBlocks = isSpotLoading || shouldRenderTopSection || shouldRenderStatsSection;

    const handlePropertyViewVisibilityChange = useCallback((isVisible: boolean) => {
        setIsPropertyViewVisible(isVisible);
    }, []);

    return (
        <VisibilityLayout
            isSuperuser={profile.superuser}
            isDemo={isDemo}
            superuserView={superuserView}
            onToggleDemo={() => setDemoMode(!isDemo)}
            onToggleSuperuserView={() => setSuperUserView(!superuserView)}
        >
            {shouldRenderVisibilityBlocks && (
                <div className={styles.container} data-testid="visibility-blocks">
                    <TopSection
                        shouldRender={shouldRenderTopSection}
                        spotUuid={spot?.uuid ?? ''}
                        spotLatitude={spot?.address?.latitude ?? ''}
                        spotLongitude={spot?.address?.longitude ?? ''}
                        isDemoMode={isDemo}
                        isPropertyViewVisible={isPropertyViewVisible}
                        onPropertyViewVisibilityChange={handlePropertyViewVisibilityChange}
                    />
                    <StatsSection
                        shouldRender={shouldRenderStatsSection}
                        spotUuid={spot?.uuid ?? ''}
                        timezone={spot?.timezone ?? 'UTC'}
                        isDemoMode={isDemo}
                    />
                </div>
            )}
            {spot && !isSpotLoading && shouldRenderDriverLog && (
                <DriverLog
                    spot={spot}
                    filters={filters}
                    urlUpdater={urlUpdater}
                    timezone={spot.timezone}
                    isSuperuserView={superuserView}
                    isEnabled={isDriverLogEnabled || isDemo}
                    isDemo={isDemo}
                />
            )}
        </VisibilityLayout>
    );
}

function mapStateToProps(state: AppState) {
    return {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        spot: selectedSpotInfo(state)!,
        loadingSpots: state.spots.orgSpotsLoading,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        profile: state.auth.profile!,
    };
}

const mapDispatchToProps = {
    push,
};

export default connect(mapStateToProps, mapDispatchToProps)(VisibilityContainerComponent);
